/**
 * RoofPreviewPage — upload an EagleView/Hover roof XML and see the 2D roof plan
 * on screen (PRP-I5), then download an importable, NATIVE-editable .ESX of it
 * (PRP-I6). Also supports describing a roof in free text ("6/12 gable 40x24") and
 * downloading the ESX directly.
 *
 *   POST /api/v1/roof/preview              -> inline SVG plan + validated totals
 *   POST /api/v1/roof/emit                 -> base64 .ESX from the uploaded XML
 *   POST /api/v1/roof/emit-from-description-> base64 .ESX from a text description
 *
 * Public route (no auth) for the local visual-proof step — this is NOT deployed.
 */
import { useState } from 'react';

import { triggerEsxDownload } from '../lib/esxDownload';

const BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://est.anterotrail.com/api/v1';

interface OracleCheck {
  label: string;
  computed: number;
  published: number;
  pct: number;
  within: boolean;
}

interface RoofPreview {
  filename: string;
  source_format: string;
  svg: string;
  total_roof_area_sf: number;
  plan_width_ft: number;
  plan_height_ft: number;
  edge_lengths_ft: Record<string, number>;
  has_summary: boolean;
  oracle_passed: boolean;
  oracle: OracleCheck[];
}

interface RoofEmit {
  filename: string;
  esx_base64: string;
  project_name: string;
  roof_kind: string;
  self_check_passed: boolean;
}

export default function RoofPreviewPage() {
  const [preview, setPreview] = useState<RoofPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [desc, setDesc] = useState('');
  const [emitting, setEmitting] = useState(false);
  const [emitError, setEmitError] = useState<string | null>(null);
  const [emitNote, setEmitNote] = useState<string | null>(null);

  async function handleFile(f: File) {
    setLoading(true);
    setError(null);
    setPreview(null);
    setEmitError(null);
    setEmitNote(null);
    setFileName(f.name);
    setFile(f);
    try {
      const form = new FormData();
      form.append('file', f);
      const resp = await fetch(`${BASE_URL}/roof/preview`, { method: 'POST', body: form });
      if (!resp.ok) {
        const detail = await resp.json().catch(() => null);
        throw new Error(detail?.detail || `Request failed (${resp.status})`);
      }
      setPreview((await resp.json()) as RoofPreview);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  async function downloadEsx(run: () => Promise<Response>) {
    setEmitting(true);
    setEmitError(null);
    setEmitNote(null);
    try {
      const resp = await run();
      if (!resp.ok) {
        const detail = await resp.json().catch(() => null);
        throw new Error(detail?.detail || `Request failed (${resp.status})`);
      }
      const data = (await resp.json()) as RoofEmit;
      triggerEsxDownload(data.filename || 'roof.ESX', data.esx_base64);
      setEmitNote(
        `Downloaded ${data.filename} — ${data.roof_kind}, geometry self-check ` +
          (data.self_check_passed ? 'passed ✓' : 'FAILED ✗'),
      );
    } catch (e) {
      setEmitError(e instanceof Error ? e.message : 'Could not generate ESX.');
    } finally {
      setEmitting(false);
    }
  }

  function downloadFromUpload() {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    downloadEsx(() => fetch(`${BASE_URL}/roof/emit`, { method: 'POST', body: form }));
  }

  function downloadFromDescription() {
    downloadEsx(() =>
      fetch(`${BASE_URL}/roof/emit-from-description`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: desc }),
      }),
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-semibold mb-1">Roof sketch → editable ESX</h1>
        <p className="text-slate-400 mb-6">
          Upload an EagleView or Hover roof report (<code>.xml</code>) to see the 2D
          plan and download an importable, editable ESX — or describe a roof in plain
          text and download the ESX directly.
        </p>

        <label className="inline-block cursor-pointer rounded-lg bg-blue-600 hover:bg-blue-500 px-5 py-2.5 font-medium transition-colors">
          {loading ? 'Rendering…' : 'Choose roof XML'}
          <input
            type="file"
            accept=".xml"
            className="hidden"
            disabled={loading}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }}
          />
        </label>
        {fileName && <span className="ml-3 text-slate-400 text-sm">{fileName}</span>}

        {/* Describe-a-roof path: no upload needed. */}
        <div className="mt-6 border-t border-slate-700 pt-6">
          <p className="text-slate-400 mb-2 text-sm">
            …or describe a roof and download an editable ESX directly:
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="text"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="6/12 gable 40x24"
              className="rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm w-72"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && desc.trim() && !emitting) downloadFromDescription();
              }}
            />
            <button
              onClick={downloadFromDescription}
              disabled={emitting || !desc.trim()}
              className="rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 px-4 py-2 text-sm font-medium transition-colors"
            >
              {emitting ? 'Generating…' : 'Download editable ESX'}
            </button>
          </div>
          <p className="text-slate-500 text-xs mt-2">
            Examples: <code>6/12 gable 40x24</code> · <code>hip 30 by 50, 4/12 pitch</code>{' '}
            · <code>shed 12x10 pitch 3</code>
          </p>
        </div>

        {emitError && (
          <div className="mt-4 rounded-lg border border-red-500/50 bg-red-500/10 px-4 py-3 text-red-300">
            {emitError}
          </div>
        )}
        {emitNote && (
          <div className="mt-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-emerald-300 text-sm">
            {emitNote}
          </div>
        )}

        {error && (
          <div className="mt-6 rounded-lg border border-red-500/50 bg-red-500/10 px-4 py-3 text-red-300">
            {error}
          </div>
        )}

        {preview && (
          <>
            <div className="mt-8 grid gap-6 md:grid-cols-[1fr_260px]">
              <div className="rounded-xl border border-slate-700 bg-white p-3 overflow-auto">
                {/* SVG is generated by our own backend from numeric geometry. */}
                <div aria-label="roof plan" dangerouslySetInnerHTML={{ __html: preview.svg }} />
              </div>

              <div className="rounded-xl border border-slate-700 bg-slate-800 p-4 text-sm">
                <div className="mb-3">
                  <div className="text-slate-400">Source</div>
                  <div className="font-medium">{preview.source_format}</div>
                </div>
                <div className="mb-3">
                  <div className="text-slate-400">Roof area</div>
                  <div className="font-medium">
                    {preview.total_roof_area_sf.toLocaleString()} sq ft
                  </div>
                </div>
                <div className="mb-3">
                  <div className="text-slate-400">Plan extent</div>
                  <div className="font-medium">
                    {preview.plan_width_ft} × {preview.plan_height_ft} ft
                  </div>
                </div>
                <div className="mb-3">
                  <div className="text-slate-400 mb-1">Edge lengths (ft)</div>
                  {Object.entries(preview.edge_lengths_ft).map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span>{k}</span>
                      <span className="font-medium">{v.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                {preview.has_summary && (
                  <div>
                    <div className="text-slate-400 mb-1">
                      Vs published{' '}
                      <span className={preview.oracle_passed ? 'text-emerald-400' : 'text-red-400'}>
                        {preview.oracle_passed ? '✓ within 2%' : '✗ off'}
                      </span>
                    </div>
                    {preview.oracle.map((c) => (
                      <div key={c.label} className="flex justify-between">
                        <span>{c.label}</span>
                        <span className={c.within ? 'text-slate-300' : 'text-red-400'}>
                          {c.pct > 0 ? '+' : ''}
                          {c.pct}%
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={downloadFromUpload}
              disabled={emitting}
              className="mt-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 px-5 py-2.5 font-medium transition-colors"
            >
              {emitting ? 'Generating…' : 'Download editable ESX'}
            </button>
            <p className="text-slate-500 text-xs mt-2">
              Imports as a native, editable roof layer (drag a vertex to confirm).
            </p>
          </>
        )}
      </div>
    </div>
  );
}
